'use client';

import { c as _c } from "react/compiler-runtime";
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useRef } from 'react';
import type { GuardaEtapas, OnboardingSelfStep } from '../types';
const PARAM_ETAPA = 'etapa';
const STEP_TO_SLUG: Record<OnboardingSelfStep, string | null> = {
  inicio: null,
  empresa: 'empresa',
  produtos: 'produtos',
  operacao: 'operacao',
  criando: 'criando',
  presente: 'presente'
};
const SLUG_TO_STEP: Record<string, OnboardingSelfStep> = {
  empresa: 'empresa',
  produtos: 'produtos',
  operacao: 'operacao',
  criando: 'criando',
  presente: 'presente'
};
const resolverEtapa = (slug: string | null, guarda: GuardaEtapas): OnboardingSelfStep => {
  const pedida: OnboardingSelfStep = slug ? SLUG_TO_STEP[slug] ?? 'inicio' : 'inicio';
  if (pedida === 'inicio' || pedida === 'empresa') return pedida;
  if (pedida === 'presente') return guarda.contaCriada ? 'presente' : 'empresa';
  // `criando` é transitória: recarregar a página nela deixaria um spinner eterno,
  // porque a criação que o spinner acompanhava morreu com a aba anterior.
  if (pedida === 'criando') return guarda.produtosEscolhidos ? 'operacao' : 'empresa';
  if (!guarda.empresaPreenchida) return 'empresa';
  if (pedida === 'operacao' && !guarda.produtosEscolhidos) return 'produtos';
  return pedida;
};

// TODO: promover o motor de sincronização step↔URL (idêntico ao de
// notificacaoSelf e negociacaoSelf) para shared/hooks, parametrizado por
// stepToSlug/slugToStep/resolverEtapa.
/**
 * `pronto` segura a primeira resolução da etapa até o rascunho da aba ser
 * hidratado: resolvida antes, a guarda ainda vazia mandaria a recarga em
 * `criando` para `empresa` em vez de `operacao`.
 */
export const useSelfStepUrl = (step, irParaEtapa, guarda, t0) => {
  const $ = _c(13);
  const pronto = t0 === undefined ? true : t0;
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  let t1;
  if ($[0] !== searchParams) {
    t1 = searchParams.get(PARAM_ETAPA);
    $[0] = searchParams;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const slugAtual = t1;
  const stepAnteriorRef = useRef(null);
  const slugAnteriorRef = useRef(null);
  const guardaRef = useRef(guarda);
  let t2;
  if ($[2] !== guarda) {
    t2 = () => {
      guardaRef.current = guarda;
    };
    $[2] = guarda;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  useEffect(t2);
  let t3;
  let t4;
  if ($[4] !== irParaEtapa || $[5] !== pathname || $[6] !== pronto || $[7] !== router || $[8] !== searchParams || $[9] !== slugAtual || $[10] !== step) {
    t3 = () => {
      if (!pronto) {
        return;
      }
      const primeiraExecucao = stepAnteriorRef.current === null;
      const stepAnterior = stepAnteriorRef.current;
      const slugAnterior = slugAnteriorRef.current;
      stepAnteriorRef.current = step;
      slugAnteriorRef.current = slugAtual;
      if (primeiraExecucao) {
        const etapaInicial = resolverEtapa(slugAtual, guardaRef.current);
        if (etapaInicial !== step) {
          irParaEtapa(etapaInicial);
        }
        return;
      }
      const slugMudou = slugAnterior !== slugAtual;
      const stepMudou = stepAnterior !== step;
      if (slugMudou && !stepMudou) {
        if (STEP_TO_SLUG[step] !== slugAtual) {
          irParaEtapa(resolverEtapa(slugAtual, guardaRef.current));
        }
        return;
      }
      if (!stepMudou) {
        return;
      }
      const slugDesejado = STEP_TO_SLUG[step];
      if (slugDesejado === slugAtual) {
        return;
      }
      const params = new URLSearchParams(searchParams.toString());
      if (slugDesejado) {
        params.set(PARAM_ETAPA, slugDesejado);
      } else {
        params.delete(PARAM_ETAPA);
      }
      const query = params.toString();
      const url = query ? `${pathname}?${query}` : pathname;
      router.replace(url, {
        scroll: false
      });
    };
    t4 = [pronto, step, slugAtual, searchParams, pathname, router, irParaEtapa];
    $[4] = irParaEtapa;
    $[5] = pathname;
    $[6] = pronto;
    $[7] = router;
    $[8] = searchParams;
    $[9] = slugAtual;
    $[10] = step;
    $[11] = t3;
    $[12] = t4;
  } else {
    t3 = $[11];
    t4 = $[12];
  }
  useEffect(t3, t4);
};